import styled from 'styled-components';
import {
  AppAudioPlayerControlsPauseFilled,
  AppAudioPlayerControlsPlayFilled,
  AppAudioPlayerEqualizer,
  AppAudioPlayerEqualizerWithoutAnimation
} from '@elfsight/icons';
import { Icon } from '@elfsight/lib-icon';
import { getHandleActionKeyDown } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { getAnimation, getPlayerControls, WHITE } from '../../../../../helpers';
import { Image, useTheme } from '../../../../common';
import { useSource } from '../../../../sources';
import { usePlayer } from '../../../PlayerProvider';
import { useFloating } from '../FloatingProvider';

export function MinimizePlayerPlayPause() {
  const {
    coverImage,
    trackElements = [],
    playerParts,
    controls: derivedControls,
    existingPlayerControls,
    playerControls
  } = useSettings();
  const { playing, togglePlaying, hasInteracted } = usePlayer();
  const { setMinimize } = useFloating();
  const { currentItem } = useSource();
  const { textColor } = useTheme();

  const { image, title } = currentItem ?? {};

  const controls = getPlayerControls(
    derivedControls,
    existingPlayerControls,
    playerControls
  );
  const withTrackControls = controls.includes('trackControls');

  function clickPlayPauseHandler() {
    if (!hasInteracted && playing) {
      return;
    }

    if (!withTrackControls) {
      setMinimize(false);
    } else {
      togglePlaying(!playing);
    }
  }

  const { url } = coverImage ?? {};

  const iconFill = trackElements.includes('image') ? WHITE : textColor;

  return (
    <StyledContainer
      tabIndex={0}
      role="button"
      onClick={clickPlayPauseHandler}
      onKeyDown={getHandleActionKeyDown(clickPlayPauseHandler)}
    >
      <StyledImageContainer
        trackName={title}
        link={url || image}
        _size={40}
        _withoutImage={!playerParts.includes('image')}
        _withTrackControls={withTrackControls}
      >
        {playing ? (
          <>
            <StyledIcon
              as={StyledIconPause}
              fill={iconFill}
              onClick={clickPlayPauseHandler}
              size={24}
            />
            <StyledIcon as={StyledIconPlaying} size={24} fill={iconFill} />
          </>
        ) : (
          <StyledIcon
            as={
              withTrackControls || playing
                ? StyledIconPlay
                : StyledIconPauseEqualizer
            }
            fill={iconFill}
            size={24}
          />
        )}
      </StyledImageContainer>
    </StyledContainer>
  );
}

const StyledIconPlaying = styled(AppAudioPlayerEqualizer)`
  opacity: 1;
  z-index: 997;
  width: 40px !important;
  height: 40px !important;
`;

const StyledIconPauseEqualizer = styled(
  AppAudioPlayerEqualizerWithoutAnimation
)`
  opacity: 1;
  z-index: 997;
  width: 40px !important;
  height: 40px !important;
`;

const StyledIconPlay = styled(AppAudioPlayerControlsPlayFilled)`
  opacity: 1;
  z-index: 999 !important;
`;

const StyledIconPause = styled(AppAudioPlayerControlsPauseFilled)`
  opacity: 0;
  z-index: 999 !important;
`;

const StyledImageContainer = styled(Image)<{
  _size: number;
  _withTrackControls: boolean;
  _withoutImage: boolean;
}>`
  position: relative;
  flex-shrink: 0;
  border-radius: 4px;
  overflow: hidden;
  width: ${({ _size }) => _size}px;
  height: ${({ _size }) => _size}px;

  &:before {
    position: absolute;
    content: ' ';
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(17, 17, 17, 0.5);
    z-index: 99;
  }

  &:hover ${StyledIconPlaying} {
    ${({ _withTrackControls }) => _withTrackControls && 'opacity: 0;'}
  }

  &:hover ${StyledIconPause}, &:hover ${StyledIconPlay} {
    ${({ _withTrackControls }) => _withTrackControls && 'opacity: 1;'}
  }

  & img {
    ${({ _withoutImage }) => _withoutImage && 'visibility: hidden;'}
  }
`;

const StyledIcon = styled(Icon)`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  transition: ${getAnimation('opacity')};
  z-index: 99;
`;

const StyledContainer = styled.div`
  cursor: pointer;
`;
